import React from 'react';
import styled from 'styled-components';

const HeroContainer = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 700px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding-top: 80px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding-top: 140px;
  }
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  transform: translate(-50%, -50%);
  z-index: 0;
  object-fit: cover;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(10, 14, 39, 0.85) 0%,
    rgba(36, 40, 51, 0.75) 100%
  );
  z-index: 1;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 900px;
  padding: 0 ${props => props.theme.spacing.large};
  animation: fadeIn 1s ease-out;
`;

const HeroTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes.mega};
  font-weight: ${props => props.theme.fontWeights.extrabold};
  margin-bottom: ${props => props.theme.spacing.large};
  text-transform: uppercase;
  letter-spacing: 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.medium};

  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.neon.cyan} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  filter: drop-shadow(0 0 20px ${props => props.theme.colors.primary});

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 3rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.large};
  flex-wrap: wrap;
`;

const LogoImage = styled.img`
  width: 120px;
  height: 120px;
  filter: drop-shadow(0 0 30px ${props => props.theme.colors.primary});
  animation: pulse 3s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      filter: drop-shadow(0 0 30px ${props => props.theme.colors.primary});
    }
    50% {
      transform: scale(1.05);
      filter: drop-shadow(0 0 40px ${props => props.theme.colors.primary});
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100px;
    height: 100px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 80px;
    height: 80px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.xlarge};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.xxlarge};
  line-height: 1.8;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${props => props.theme.fontSizes.large};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.medium};
  justify-content: center;
  flex-wrap: wrap;
`;

const HeroButton = styled.a<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-block;
  padding: ${props => props.theme.spacing.medium} ${props => props.theme.spacing.xlarge};
  font-size: ${props => props.theme.fontSizes.large};
  font-weight: ${props => props.theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: ${props => props.theme.borderRadius.medium};
  transition: all ${props => props.theme.transitions.medium};
  cursor: pointer;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, ${props.theme.colors.primary} 0%, ${props.theme.colors.primaryDark} 100%);
    color: ${props.theme.colors.background.primary};
    box-shadow: ${props.theme.shadows.glow};

    &:hover {
      transform: translateY(-3px);
      box-shadow: ${props.theme.shadows.glowStrong};
    }
  ` : `
    background: transparent;
    border: 2px solid ${props.theme.colors.primary};
    color: ${props.theme.colors.primary};

    &:hover {
      background: ${props.theme.colors.primary};
      color: ${props.theme.colors.background.primary};
      transform: translateY(-3px);
    }
  `}

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.large};
    font-size: ${props => props.theme.fontSizes.medium};
  }
`;

const HeroSection: React.FC = () => {
  return (
    <HeroContainer>
      <VideoBackground autoPlay loop muted playsInline>
        <source src="/assets/videos/hero-background.mp4" type="video/mp4" />
      </VideoBackground>

      <Overlay />

      <HeroContent>
        <HeroTitle>
          <TitleRow>
            YOUR FASTEST
          </TitleRow>
          <TitleRow>
            ASSISTANT
            <LogoImage src="/assets/images/logo.png" alt="NeuralPrompt" />
          </TitleRow>
          <TitleRow>
            IN PROMPT CREATION
          </TitleRow>
        </HeroTitle>
        <HeroSubtitle>
          NEURALPROMPT is a powerful prompt editing tool designed to simplify
          the preparation of prompts and scripts intended for use with Midjourney on Discord.
        </HeroSubtitle>

        <ButtonGroup>
          <HeroButton href="#versions" $variant="primary">
            Download Trial
          </HeroButton>
          <HeroButton href="#versions" $variant="primary">
            Download PRO
          </HeroButton>
          <HeroButton href="#features" $variant="secondary">
            Learn More
          </HeroButton>
        </ButtonGroup>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;
